import {
  ChartSpline,
  Home,
  Package,
  Settings,
  ShoppingCart,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";

/**
 * Single source of truth for the application navigation tree.
 *
 * Consumed by:
 *  - components/body/NavMenu.jsx        (classic sidebar)
 *  - layout/TopNav/TopMenu.jsx          (modern top menu)
 *  - pages/Home/ModernHome              (launchpad tiles)
 *
 * Returns an array of groups:
 *   { key, title, icon, path?, children?: [{ title, path }] }
 *
 * A group with `path` and no `children` is a direct link.
 * Access/feature gating is identical to the original NavMenu.jsx.
 *
 * Module order & terminology follow mainstream ERP convention
 * (Odoo / Dynamics 365 BC / Zoho):
 *   Home → Sales → Purchasing → Inventory → Accounting → CRM → Reports → Employees → Settings
 */
const iconCls = "text-[22px] text-slate-600";

// badges: optional { [key]: number } of counts to show on a menu row, e.g.
// { stockRequests: 3 }. Passed only by NavMenu — the search index, shortcuts and
// launchpad all call buildMenu too and have no use for a count, so it defaults to
// empty and every other caller is unchanged.
export const buildMenu = (access = {}, settings = {}, t = (k) => k, badges = {}) => {
  const groups = [];

  // ----- Home -----
  if (access.dashboardAccess) {
    groups.push({
      key: "home",
      title: t("home"),
      path: "home",
      icon: <Home className={iconCls} />,
    });
  }

  // Everything below requires an active fiscal year (same as classic menu).
  // Exception: a freshly-provisioned org has fyAccess but no fiscal year yet —
  // the /fiscal_year route is intentionally available pre-FY (accountsRoutes.js
  // registers it before its own fiscalYearId guard), so surface just that item
  // here; otherwise onboarding is a dead-end (only Home, no way to create the
  // first fiscal year from the menu).
  if (!settings?.fiscalYearId) {
    if (access?.fyAccess) {
      groups.push({
        key: "accounts",
        title: t("accounts"),
        icon: <Wallet className={iconCls} />,
        children: [{ title: t("fiscalYear"), path: "fiscal_year" }],
      });
    }
    return groups;
  }

  // The generic Services module (Services, Service Categories, Service Discount
  // Types, Service Contracts) used to need `settings.enableServices` on top of its
  // own access flag. That switch was removed 2026-08-02 — it duplicated the
  // permission and won the tie, so granting the permission left the menu hidden.
  // Each of those items is now gated on its access flag alone.
  //
  // Car-Service vertical — gates Service Bays and Vehicles (Vehicle Brands,
  // customer vehicles). Stays on BusinessType; it is a different question from
  // "may this user see the Services screens" and must NOT be folded in.
  const isCarService = settings?.businessType === 1;

  // ----- Sales -----
  if (
    access.posAccess ||
    access.salesOrderAccess ||
    access.salesInvoiceAccess ||
    access.salesHistoryAccess ||
    access.customerAccess ||
    access.customerGroupAccess ||
    access.couponAccess ||
    access.wastageAccess ||
    access.serviceContractAccess ||
    // Contract Billing is its own grant: an accountant may hold it without any
    // rights over the contract records themselves, and still needs the group open.
    access.contractBillingAccess
  ) {
    const children = [
      ...(access.posAccess
        ? [
            { title: t("pos"), path: "pos" },
            ...(access.pos?.registerClosing
              ? [{ title: t("closeRegister"), path: "close_reg" }]
              : []),
            ...(access.pos?.quote
              ? [{ title: t("quotationSales"), path: "quotes" }]
              : []),
          ]
        : []),
      ...(access.salesOrderAccess
        ? [{ title: t("salesOrder"), path: "sales_order" }]
        : []),
      ...(access.salesInvoiceAccess
        ? [{ title: t("salesInvoice"), path: "sales_invoice" }]
        : []),
      ...(access.salesHistoryAccess
        ? [{ title: t("salesHistory"), path: "sales_history" }]
        : []),
      ...(access.pos?.returnSale
        ? [{ title: t("salesReturns"), path: "sale_returns" }]
        : []),
      ...(access.wastageAccess
        ? [{ title: t("stockWastage"), path: "stock_wastage" }]
        : []),
      ...(access.customerAccess
        ? [{ title: t("customers"), path: "customers" }]
        : []),
      ...(access.customerGroupAccess
        ? [{ title: t("customersGroup"), path: "customer_groups" }]
        : []),
      ...(access.couponAccess
        ? [{ title: t("coupons", "Coupons"), path: "coupons" }]
        : []),
      // Recurring service billing — TWO independent grants, deliberately.
      // serviceContractAccess is authority over the agreements; contractBillingAccess
      // is authority over the queue that turns due periods into real invoices against
      // AR and the GL. A sales rep gets the first, an accountant the second.
      ...(access.serviceContractAccess
        ? [
            {
              title: t("serviceContracts", "Service Contracts"),
              path: "service_contracts",
            },
          ]
        : []),
      ...(access.contractBillingAccess
        ? [
            {
              title: t("contractBillingRun", "Contract Billing"),
              path: "contract_billing_run",
            },
          ]
        : []),
    ];
    groups.push({
      key: "sales",
      title: t("sales"),
      icon: <TrendingUp className={iconCls} />,
      children,
    });
  }

  // ----- Purchasing -----
  // Three independent documents, each always available (no mode toggle): the store owner
  // uses whichever they need. Purchase Order (commitment) → Goods Receipt (stock in → GRNI)
  // → Purchase Invoice (the bill → creates the payable; standalone or clearing a receipt).
  // The create forms are routed under stock.create — gate the same way or a view-only user 404s.
  if (
    access.supplierAccess ||
    access.stockAccess ||
    access.goodsReceiptAccess ||
    access.supplierBillAccess ||
    access.stockReturnAccess
  ) {
    const children = [
      ...(access.supplierAccess ? [{ title: t("suppliers"), path: "suppliers" }] : []),
      // Purchase Order register (the list). Creating a new PO is the "New" button inside the
      // list header — no separate create-form menu item (merged, same as Goods Receipts).
      ...(access.stockAccess
        ? [{ title: t("purchaseOrder"), path: "purchase_orders" }]
        : []),
      // The supplier bill — creates the payable. Standalone (one-step: stock in + bill) or
      // clears a Goods Receipt. This is what most simple clients use on its own.
      ...(access.supplierBillAccess
        ? [{ title: t("purchaseInvoices", "Purchase Invoices"), path: "purchase_invoices" }]
        : []),
      // Goods Receipt register — every posted GRN (PO-linked + standalone).
      ...(access.goodsReceiptAccess
        ? [{ title: t("goodsReceipts", "Goods Receipts"), path: "goods_receipts" }]
        : []),
      ...(access.stockReturnAccess
        ? [{ title: t("returnStock"), path: "purchase_returns" }]
        : []),
    ];
    groups.push({
      key: "purchase",
      title: t("purchase"),
      icon: <ShoppingCart className={iconCls} />,
      children,
    });
  }

  // ----- Inventory -----
  if (
    access.printLabelAccess ||
    access.catalogAccess ||
    access.categoryAccess ||
    access.brandAccess ||
    access.tagAccess ||
    access.productAttributeAccess ||
    access.inventoryCountAccess ||
    access.inventoryAdjustmentAccess ||
    access.stockTransferAccess ||
    access.serviceAccess ||
    access.serviceCategoryAccess
  ) {

    const children = [
      ...(access.catalogAccess ? [{ title: t("products"), path: "products" }] : []),
      // (Purchases register moved to the Purchasing section.)
      // Two entries, because they answer two different questions: "what has someone
      // asked me for" and "what is on the road right now".
      //
      // EACH carries its own count, and that is what keeps the split honest. A request
      // nobody has sent against yet appears on the first and nowhere on the second, so
      // an empty Transfers screen once read as "my request is lost" — with the counts
      // in the menu, the other screen says where the work actually is.
      //
      // The badges ride on the SAME access gate as the rows — inside this ternary, not
      // applied afterwards — so a user without stockTransferAccess is never told how
      // much work exists.
      ...(access.stockTransferAccess
        ? [
            {
              title: t("stockRequests"),
              path: "stock_requests",
              badge: badges.stockRequests,
            },
            {
              title: t("stockTransfers"),
              path: "stock_transfer_history",
              badge: badges.stockShipments,
            },
          ]
        : []),

      ...(access?.inventoryCountAccess
        ? [{ title: t("inventoryCounts"), path: "inventory_counts" }]
        : []),
      ...(access?.inventoryAdjustmentAccess
        ? [{ title: t("inventoryAdjustments"), path: "inventory_adjustments" }]
        : []),
      ...(access.printLabelAccess
        ? [{ title: t("printLabels"), path: "print_label_history" }]
        : []),
      ...(access.categoryAccess
        ? [{ title: t("category"), path: "product_category" }]
        : []),
      ...(access.brandAccess ? [{ title: t("brands"), path: "brands" }] : []),
      ...(access.tagAccess ? [{ title: t("tags"), path: "tags" }] : []),
      ...(access.productAttributeAccess
        ? [{ title: t("productAttributes"), path: "product_attributes" }]
        : []),
      ...(access.catalogAccess
        ? [{ title: t("prodPriceType"), path: "price_categories" }]
        : []),
      ...(access.catalogAccess
        ? [{ title: t("unitManagement"), path: "unit_management" }]
        : []),
      ...(access.serviceAccess
        ? [{ title: t("services"), path: "services" }]
        : []),
      ...(access.serviceCategoryAccess
        ? [{ title: t("serviceCategories"), path: "service_categories" }]
        : []),
    ];
    groups.push({
      key: "inventory",
      title: t("inventory"),
      icon: <Package className={iconCls} />,
      children,
    });
  }

  // ----- Accounting -----
  if (
    access?.journalVoucherAccess ||
    access?.expenseAccess ||
    access?.cashMgmtAccess ||
    access?.reportAccess ||
    access?.coaAccess ||
    access?.accountMappingAccess ||
    access?.bankReconciliationAccess ||
    access?.accountTransferAccess ||
    access?.bankAccountAccess ||
    access?.cashBankDashboardAccess ||
    access?.cashSafeAccess ||
    access?.branchCashSafeAccess ||
    access?.fyAccess
  ) {
    const children = [
      // Transactions
      ...(access?.journalVoucherAccess
        ? [{ title: t("journalVoucher"), path: "journal_voucher" }]
        : []),
      ...(access?.expenseAccess
        ? [{ title: t("expenses"), path: "expenses" }]
        : []),
      ...(access?.cashMgmtAccess
        ? [{ title: t("cashManagement"), path: "cash_management" }]
        : []),
      ...(access?.branchCashSafeAccess
        ? [{ title: t("myCashSafe"), path: "my_cash_safe" }]
        : []),
      ...(access?.cashSafeAccess
        ? [{ title: t("cashSafeManagement"), path: "cash_safes" }]
        : []),
      // Banking / treasury — each on its own grant. These used to ride on
      // `cashMgmtAccess` (and `coaAccess`), which meant letting a cashier drop
      // cash into the till also showed them every bank account and transfer.
      ...(access?.cashBankDashboardAccess
        ? [{ title: t("cashAndBankOverview"), path: "cash_bank_dashboard" }]
        : []),
      ...(access?.accountTransferAccess
        ? [{ title: t("accountTransfers"), path: "account_transfers" }]
        : []),
      ...(access?.bankAccountAccess
        ? [{ title: t("bankAccounts"), path: "bank_accounts" }]
        : []),
      ...(access?.bankReconciliationAccess
        ? [{ title: t("bankReconciliation"), path: "bank_reconciliation" }]
        : []),
      // Ledgers & financial statements
      ...(access?.coaAccess
        ? [{ title: t("coaManagement"), path: "coa_management" }]
        : []),
      ...(access?.accountMappingAccess
        ? [{ title: t("accountMapping", "Account Mapping"), path: "account_mapping" }]
        : []),
      ...(access?.reportAccess
        ? [
            ...(access.report?.generalLedger
              ? [
                  { title: t("generalLedger"), path: "general_ledger" },
                  { title: t("journalEntries"), path: "journal_entries" },
                ]
              : []),
            ...(access.report?.trialBalance
              ? [{ title: t("trialBalance"), path: "trial_balance" }]
              : []),
            ...(access.report?.balanceSheet
              ? [{ title: t("balanceSheet"), path: "balance_sheet" }]
              : []),
            ...(access.report?.profitAndLoss
              ? [
                  { title: t("profitAndLoss"), path: "profit_and_loss" },
                  { title: t("cashFlow"), path: "cash_flow" },
                ]
              : []),
            ...(access.report?.supplierAging
              ? [{ title: t("supplierAging", "Supplier Aging"), path: "supplier_aging" }]
              : []),
            ...(access.report?.customerAging
              ? [{ title: t("customerAging", "Customer Aging"), path: "customer_aging" }]
              : []),
          ]
        : []),
      // Period
      ...(access?.fyAccess
        ? [{ title: t("fiscalYear"), path: "fiscal_year" }]
        : []),
    ];
    groups.push({
      key: "accounts",
      title: t("accounts"),
      icon: <Wallet className={iconCls} />,
      children,
    });
  }

  // ----- CRM -----
  if (
    access.inqueryAccess ||
    access.followUpTypeAccess ||
    access.inquerySourceAccess
  ) {
    const children = [
      ...(access.inqueryAccess ? [{ title: t("inquiries"), path: "inquiries" }] : []),
      ...(access.inquerySourceAccess
        ? [{ title: t("inquerySources"), path: "inquery_sources" }]
        : []),
      ...(access.followUpTypeAccess
        ? [{ title: t("followUpTypes"), path: "fullow_up_types" }]
        : []),
    ];
    groups.push({
      key: "crm",
      title: t("crm"),
      icon: <Users className={iconCls} />,
      children,
    });
  }

  // ----- Reports -----
  if (access.reportAccess) {
    const children = [
      ...(access.report?.businessActivity
        ? [{ title: t("businessActivity", "Business Activity"), path: "business_activity" }]
        : []),
      // Sales Reports (/sales_reports) retired 2026-07-28 — it rendered Cost of
      // Goods Sold, Gross Profit and Margin % behind bare `reportAccess`, with no
      // per-report flag of its own. Use Sales Summary / Profit by Invoice instead,
      // which are individually grantable. The API is closed too (SalesReportController).
      ...(access.report?.salesSummary
        ? [{ title: t("salesSummary", "Sales Summary"), path: "sales_summary" }]
        : []),
      ...(access.report?.itemWiseSales
        ? [{ title: t("itemWiseSales"), path: "item_wise_sales" }]
        : []),
      ...(access.report?.productMovement
        ? [{ title: t("productPerform"), path: "product_perform_report" }]
        : []),
      ...(access.report?.profitByInvoice
        ? [{ title: t("profitByInvoice"), path: "profit_by_invoice" }]
        : []),
      ...(access.report?.customerWiseProfitLoss
        ? [{ title: t("customerWisePL"), path: "customer_wise_pl" }]
        : []),
      ...(access.report?.creditCustomer
        ? [{ title: t("creditCustomer"), path: "credit_customer_report" }]
        : []),
      ...(access.report?.expenseReport
        ? [{ title: t("expenseReport"), path: "expense_report" }]
        : []),
      ...(access.report?.paymentReport
        ? [{ title: t("paymentReports"), path: "payment_reports" }]
        : []),
      ...(access.report?.taxReport
        ? [{ title: t("taxReports"), path: "tax_reports" }]
        : []),
      ...(access.report?.stockReport
        ? [{ title: t("stockReport", "Stock Report"), path: "stock_report" }]
        : []),
      ...(access.report?.productMovement
        ? [{ title: t("stockMovements"), path: "stock_movements_report" }]
        : []),
      ...(access.report?.inventoryAdjustmentReport
        ? [
            {
              title: t("inventoryAdjustmentReport", "Inventory Adjustment Report"),
              path: "inventory_adjustment_report",
            },
          ]
        : []),
      ...(access.report?.registerClosure
        ? [{ title: t("registerClosures"), path: "register_closures" }]
        : []),
      ...(access.report?.userReport
        ? [{ title: t("userReports"), path: "user_reports" }]
        : []),
      ...(access.commissionAccessNav?.report
        ? [{ title: t("commissionReport"), path: "commission_report" }]
        : []),
      ...(access.report?.logReport
        ? [{ title: t("logReport", "Log Report"), path: "log_report" }]
        : []),
    ];
    groups.push({
      key: "reports",
      title: t("reports"),
      icon: <ChartSpline className={iconCls} />,
      children,
    });
  }

  // ----- Employees -----
  if (access.employeeAccess || access.commissionAccessNav?.setup) {
    const children = [
      ...(access.employeeAccess ? [{ title: t("employees"), path: "employees" }] : []),
      ...(access.commissionAccessNav?.setup
        ? [{ title: t("commissionSetup"), path: "commission_setup" }]
        : []),
    ];
    groups.push({
      key: "employees",
      title: t("employees"),
      icon: <UserCog className={iconCls} />,
      children,
    });
  }

  // ----- Settings -----
  if (
    access.generalAccess ||
    access.status ||
    // >= 2, not === 2: tier 3 (System Engineer) is above System Admin and must not
    // lose the Settings group by failing an equality test.
    Number(settings?.userType) >= 2 ||
    access.outletAccess ||
    access.companyAccess ||
    access.paymentTypeAccess ||
    access.currencyAccess ||
    access.taxRateAccess ||
    access.cityAccess ||
    (isCarService && access.vehicleBrandAccess) ||
    (isCarService && access.serviceBayAccess) ||
    access.serviceDiscountTypeAccess ||
    access.scaleBarcodeAccess ||
    access.businessHoursAccess ||
    access.invoiceTemplateAccess ||
    access.userAccess
  ) {
    const children = [
      ...(access.generalAccess ? [{ title: t("orgInfo"), path: "org_info" }] : []),
      ...(access.companyAccess ? [{ title: t("company"), path: "companies" }] : []),
      ...(access.outletAccess ? [{ title: t("outlets"), path: "outlets" }] : []),
      ...(access.paymentTypeAccess
        ? [{ title: t("paymentTypes"), path: "payment_types" }]
        : []),
      ...(access.currencyAccess ? [{ title: t("currency"), path: "currencies" }] : []),
      ...(access.taxRateAccess ? [{ title: t("taxes"), path: "taxes" }] : []),
      ...(access.cityAccess ? [{ title: t("cities"), path: "cities" }] : []),
      ...(isCarService && access.vehicleBrandAccess
        ? [{ title: t("vehicleBrands"), path: "vehicle_brands" }]
        : []),
      ...(isCarService && access.serviceBayAccess
        ? [{ title: t("serviceBays"), path: "service_bays" }]
        : []),
      ...(access.serviceDiscountTypeAccess
        ? [{ title: t("serviceDiscountTypes", "Service Discount Types"), path: "service_discount_types" }]
        : []),
      ...(access.scaleBarcodeAccess
        ? [{ title: t("weighingScale", "Weighing Scale"), path: "weighing_scale" }]
        : []),
      ...(access.businessHoursAccess
        ? [{ title: t("businessHours", "Business Hours"), path: "business_hours" }]
        : []),
      ...(access.invoiceTemplateAccess
        ? [{ title: t("invoiceTemplates", "Invoice Templates"), path: "invoice_templates" }]
        : []),
      ...(access.userAccess ? [{ title: t("users"), path: "users" }] : []),
      // Roles & Permissions (the access-profile catalogs) is a platform-owner tool —
      // System-Admin and above manage it, regardless of the userRoleAccess flag.
      ...(Number(settings?.userType) >= 2
        ? [{ title: t("rolesPermissions", "Roles & Permissions"), path: "roles_permissions" }]
        : []),
      // SMTP + two-factor administration. Tier 3 ONLY — a System Admin must not see
      // these, which is why the test is exact rather than >=.
      ...(Number(settings?.userType) === 3
        ? [
            { title: t("mailSettings", "Mail Settings"), path: "platform_email" },
            { title: t("securitySettings", "Security & 2FA"), path: "platform_security" },
            { title: t("desktopApp", "Desktop App"), path: "desktop_app" },
            { title: t("maintenanceWindow", "Maintenance Window"), path: "maintenance_window" },
          ]
        : []),
      ...(access.status ? [{ title: t("status"), path: "status" }] : []),
      ...(access.companyAccess
        ? [{ title: t("syncInventory"), path: "sync_inventory" }]
        : []),
    ];
    groups.push({
      key: "settings",
      title: t("settings"),
      icon: <Settings className={iconCls} />,
      children,
    });
  }

  return groups;
};

// The user's default landing route = the first navigable destination in their
// own menu (which already reflects their permissions, in priority order).
// Returns null when the user has no accessible module at all. Single source of
// truth — never hardcode "/home" for landing/redirects.
export const getDefaultRoute = (access = {}, settings = {}) => {
  const menu = buildMenu(access, settings);
  for (const group of menu) {
    if (group.path) return `/${group.path}`;
    const child = group.children?.find((c) => c.path);
    if (child) return `/${child.path}`;
  }
  return null;
};

export default buildMenu;
